import React, { FC, useCallback, useMemo, useRef } from 'react';
import useConfig from 'AppShell/hooks/useConfig';
import { getShortenedCountryCode } from 'AppShell/utils/country/getShortenedCountryCode';
import styled from 'styled-components';
import ClimateNeutralLogo from './ClimateNeutralLogo';
import { forDesktop } from '@westwing/ui-kit/breakPoints';
import useTranslator from 'Client/hooks/useTranslator';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import loadable from '@loadable/component';
import { useCurrentPageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import useInView from 'Client/hooks/dom/useInView';
import { caption1 } from '@westwing/ui-kit/typography';
import useOwwFooterTracking, {
    FOOTER_TRACKING_EVENT_CATEGORY,
} from 'OneWestwingFooter/providers/OwwFooterTrackingProvider';

const ClimateNeutralTooltipWrapper = styled.div`
    display: none;
    ${forDesktop`
        left: -268px;
        bottom: 6px;
        position: absolute;
    `}
`;

const ClimateNeutralInfoWrapper = styled.div`
    position: relative;
    display: inline-block;
    ${forDesktop`
        &:hover ${ClimateNeutralTooltipWrapper} {
            display: inline-block;
        }
    `}
`;
const ClimateNeutralInfoFootnote = styled.div`
    ${caption1}
    line-height: 18px;
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.charcoal60};
    position: relative;
    ${forDesktop`
        display: none;
    `}
`;

const ClimateNeutralTooltip = loadable(() => import('./ClimateNeutralTooltip'));

const ClimateNeutralInfo: FC = () => {
    const { countryCode } = useConfig();
    const shortenedCountryCode = useMemo(() => getShortenedCountryCode(countryCode), [countryCode]);
    const t = useTranslator();
    const carbonNeutralFootnote = t('Footer_climate_neutral_footnote');
    const { p100ClimateNeutralFootNote } = useFeatureToggle();

    const { pageType } = useCurrentPageType();
    const { ga } = useOwwFooterTracking();

    const trackClick = useCallback(() => {
        ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
            action: 'Climate neutral logo - click',
            label: pageType,
        });
    }, [pageType]);

    const trackImpression = useCallback(() => {
        ga.trackNonInteractiveGenericEvent(FOOTER_TRACKING_EVENT_CATEGORY, {
            action: 'Climate neutral footnote - impression',
            label: pageType,
        });
    }, [pageType]);

    const tooltipRef = useRef<HTMLDivElement>(null);
    useInView({
        containerRef: tooltipRef,
        options: { threshold: 0.5 },
        onVisibleCallback: trackImpression,
    });

    return (
        <ClimateNeutralInfoWrapper data-testid="oww-footer-climateinfo">
            <ClimateNeutralLogo
                width={149}
                height={44}
                useNewLogo={p100ClimateNeutralFootNote}
                handleClick={trackClick}
                countrycode={shortenedCountryCode}
            />
            {p100ClimateNeutralFootNote && (
                <>
                    <ClimateNeutralInfoFootnote>{carbonNeutralFootnote}</ClimateNeutralInfoFootnote>
                    <ClimateNeutralTooltipWrapper ref={tooltipRef} role="tooltip">
                        <ClimateNeutralTooltip text={carbonNeutralFootnote} />
                    </ClimateNeutralTooltipWrapper>
                </>
            )}
        </ClimateNeutralInfoWrapper>
    );
};

export default ClimateNeutralInfo;
